/**
 * Storing a long text beside a published app.
 *
 * Web-owned for the usual reason: the shared `@/webapp/textAsset` reads its token from
 * CognitoService, which since SEC-14 reaches expo-secure-store and therefore React Native.
 * The DECISIONS are still shared — the name comes from `textAssetName` and the parts from
 * `previewParts`, both of which are the server's own functions, so the number of parts this
 * client shows is the number the server will actually store.
 *
 * The text goes to the CATALOGUE app id, because that is the id the published page is served
 * with, and therefore the id `S.asset(name)` resolves against at runtime.
 */
import { textAssetName } from '@/webapp/documentSplit';
import { ApiError, request } from './client';

export interface TextAssetInput {
  /** Display title, e.g. "The Qur'an (Sahih International)". */
  title: string;
  /** Where it came from. The server refuses without it, and so does this. */
  attribution: string;
  /** The whole document. */
  text: string;
  /** How to cut it into parts. Omitted means fixed-size chunks. */
  headingPattern?: string;
  /** Overrides the name derived from the title. */
  name?: string;
}

export interface StoredText {
  name: string;
  parts: number;
  chars: number;
}

/**
 * A document can be large, so this gets its own deadline: the 15s data default aborts a
 * healthy upload of a book.
 */
const TEXT_TIMEOUT_MS = 120_000;

export async function uploadTextAsset(hostedAppId: string | undefined, input: TextAssetInput): Promise<StoredText> {
  if (!hostedAppId) {
    throw new ApiError('misconfigured', 'This app has not been published yet, so there is nowhere to put the text.');
  }
  if (!input.text.trim()) throw new ApiError('refused', 'That document has no text in it.');
  /*
   * Checked here as well as on the server so the person is told while they can still fix it,
   * rather than after a build they have already paid for.
   */
  if (!input.attribution.trim()) {
    throw new ApiError('refused', 'A text has to say where it came from.');
  }

  const name = textAssetName(input.name ?? input.title);
  if (!name) throw new ApiError('refused', 'That title does not make a usable name.');

  return request<StoredText>(`/apps/${encodeURIComponent(hostedAppId)}/text`, {
    method: 'POST',
    timeoutMs: TEXT_TIMEOUT_MS,
    body: {
      name,
      title: input.title.trim(),
      attribution: input.attribution.trim(),
      text: input.text,
      ...(input.headingPattern ? { headingPattern: input.headingPattern } : {}),
    },
  });
}
